import { useCallback, useMemo, useRef, useState } from 'react';

import { ActionEntityType, FieldChangeResponse, changeLogGetEntityChanges, intermediaryGetById, lenderFirmsGetLenderFirm } from '@pmp/api';

import { ChangeLogCollectionChange, ChangeLogDetail, ChangeLogFieldChange, ChangeLogTarget, ChangeLogValue } from './interfaces';
import { formatValue, parseFieldPath } from './fields';
import { getSubTables } from './subTables';

/** Names of the record's collection items as it stands now, by collection and then by item key. */
type ItemNames = Record<string, Record<string, string>>;

const EMPTY_DETAIL: ChangeLogDetail = { changes: [], collections: [], failed: true };

const byKey = <T>(
  items: T[] | null | undefined,
  key: (item: T) => string | null | undefined,
  name: (item: T) => string | null | undefined,
) =>
  Object.fromEntries((items ?? []).flatMap(item => (key(item) && name(item) ? [[key(item), name(item)]] : []))) as Record<string, string>;

const loadItemNames = async (target: ChangeLogTarget): Promise<ItemNames> => {
  if (target.entityType === ActionEntityType.LENDER_FIRMS) {
    const { data } = await lenderFirmsGetLenderFirm({ path: { firmId: target.id } });

    return {
      tradingAddresses: byKey(
        data.tradingNames,
        item => item.id,
        item => item.name,
      ),
    };
  }

  if (target.entityType === ActionEntityType.BROKERS) {
    const { data } = await intermediaryGetById({ path: { id: target.id } });

    return {
      assistants: byKey(
        data.assistants,
        item => item.assistantId,
        item => item.displayName,
      ),
      advisers: byKey(
        data.advisers,
        item => item.adviserId,
        item => item.displayName,
      ),
    };
  }

  return {};
};

const firstValue = (value: ChangeLogFieldChange['newValue']): string | null => (Array.isArray(value) ? (value[0] ?? null) : value);

const sameValue = (a: ChangeLogValue, b: ChangeLogValue) => JSON.stringify(a) === JSON.stringify(b);

/** Where the grid shows each slot of an address's fixed five lines, for the addresses it shows them for. */
const lineColumn = (field: string, collection: string | undefined): ((slot: number) => string) | null => {
  if (field === 'firmAddressLineItems') return slot => `firmAddressLine${slot}`;
  if (collection === 'tradingAddresses' && field === 'tradingAddresses.lineItems') return slot => `tradingAddresses.line${slot}`;

  return null;
};

/** One change per address line that moved, so a value moved between lines or into the county shows. */
const splitLines = (field: string, collection: string | undefined, previousValue: unknown, newValue: unknown) => {
  const column = lineColumn(field, collection);
  if (!column) return [{ field, previousValue: formatValue(previousValue), newValue: formatValue(newValue) }];

  const before = Array.isArray(previousValue) ? previousValue : [];
  const after = Array.isArray(newValue) ? newValue : [];

  return Array.from({ length: Math.max(before.length, after.length) }, (_, index) => ({
    field: column(index + 1),
    previousValue: formatValue(before[index]),
    newValue: formatValue(after[index]),
  }));
};

/**
 * Splits one change request's changes into the record's own fields and one group per collection. A
 * collection item is named by its label field when the change carries it, then by the name the record
 * gives it today, and by its key when it no longer exists.
 */
export const toDetail = (
  target: ChangeLogTarget,
  actionId: string,
  fieldChanges: FieldChangeResponse[],
  itemNames: ItemNames,
): ChangeLogDetail => {
  const subTables = getSubTables(target.entityType);
  const changes: ChangeLogFieldChange[] = [];
  const byCollection = new Map<string, (ChangeLogFieldChange & { itemKey: string })[]>();

  fieldChanges.forEach(({ fieldName, previousValue, newValue }, index) => {
    const { field, collection, itemKey, excluded } = parseFieldPath(fieldName);
    if (excluded) return;

    // A value the backend saw change but that reads the same, such as no lines and five empty ones, is left out.
    splitLines(field, collection, previousValue, newValue)
      .filter(line => !sameValue(line.previousValue, line.newValue))
      .forEach((line, slot) => {
        // A procuration fee row repeats per item, so the path alone does not identify a change.
        const change = { id: `${actionId}-${index}-${slot}`, ...line };

        if (collection && itemKey !== undefined && subTables[collection]) {
          const items = byCollection.get(collection) ?? [];
          items.push({ ...change, itemKey });
          byCollection.set(collection, items);
        } else {
          changes.push(change);
        }
      });
  });

  const collections: ChangeLogCollectionChange[] = [...byCollection].map(([collection, items]) => {
    const labelField = `${collection}.${subTables[collection].rowLabel}`;
    const labels = new Map(
      items
        .filter(item => item.field === labelField)
        .map(item => [item.itemKey, firstValue(item.newValue) ?? firstValue(item.previousValue)]),
    );

    return {
      field: collection,
      changes: items.map(({ itemKey, ...item }) => ({ ...item, item: labels.get(itemKey) ?? itemNames[collection]?.[itemKey] ?? itemKey })),
    };
  });

  return { changes, collections };
};

/**
 * Loads what one change did to the record, once per change, when its row is expanded. The detail shows
 * as soon as its changes arrive; collection items are renamed once the record's item names follow.
 */
export const useChangeDetail = (target: ChangeLogTarget | null) => {
  const [loaded, setLoaded] = useState<Record<string, FieldChangeResponse[] | null>>({});
  const [itemNames, setItemNames] = useState<ItemNames>({});
  const requested = useRef(new Set<string>());
  const controller = useRef(new AbortController());
  const namesRequested = useRef(false);

  const loadDetail = useCallback(
    async (actionId: string) => {
      if (!target || requested.current.has(actionId)) return;
      requested.current.add(actionId);
      const { signal } = controller.current;

      // A retry after a failure shows as loading again.
      setLoaded(current =>
        current[actionId] === null ? Object.fromEntries(Object.entries(current).filter(([id]) => id !== actionId)) : current,
      );

      // Read once per record. When it fails, an item shows its key.
      if (!namesRequested.current) {
        namesRequested.current = true;
        loadItemNames(target)
          .then(names => !signal.aborted && setItemNames(names))
          .catch(() => undefined);
      }

      try {
        const { data } = await changeLogGetEntityChanges({ path: { actionId }, query: { entityId: target.id }, signal });
        if (!signal.aborted) setLoaded(current => ({ ...current, [actionId]: data }));
      } catch {
        if (signal.aborted) return;
        // Expanding the row again retries.
        requested.current.delete(actionId);
        setLoaded(current => ({ ...current, [actionId]: null }));
      }
    },
    [target],
  );

  const detail = useMemo(
    () =>
      target
        ? Object.fromEntries(
            Object.entries(loaded).map(([actionId, changes]) => [
              actionId,
              changes ? toDetail(target, actionId, changes, itemNames) : EMPTY_DETAIL,
            ]),
          )
        : {},
    [target, loaded, itemNames],
  ) as Record<string, ChangeLogDetail>;

  const reset = useCallback(() => {
    controller.current.abort();
    controller.current = new AbortController();
    requested.current.clear();
    namesRequested.current = false;
    setLoaded({});
    setItemNames({});
  }, []);

  return { detail, loadDetail, reset };
};
